/**
 * Pure business rules for marker mode and visibility resolution.
 *
 * Why this file exists:
 * - Separates display rules from the wiring concerns in markers.plugin.js.
 * - All functions here are pure: no side effects, no lifecycle dependencies.
 * - Rules can be tested and reasoned about independently of plugin state.
 */

import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from '../constants';
import SitePlanType from '../enums/sitePlanType';
import MarkerType from '../enums/markerType';

/**
 * Zoom level at which paid markers switch from pin to price display mode.
 */
export const PRICE_PIN_ZOOM_LEVEL = 13;

/**
 * Minimum zoom level at which the Community ISM indicator appears on pins.
 * This can be adjusted as needed to match UX requirements.
 */
export const INTERACTIVE_SITE_MAP_ZOOM = 10;

/**
 * Minimum zoom level at which basic markers become visible.
 * Only enforced when the location is Market or City and there are more than
 * PAID_MARKERS_THRESHOLD paid results.
 */
export const BASIC_PIN_ZOOM_LEVEL = 12;

/**
 * Number of paid markers above which the zoom threshold is applied for basic
 * markers in Market/City locations.
 */
export const PAID_MARKERS_THRESHOLD = 25;

/**
 * Resolves the display mode for a marker at the current zoom and context.
 *
 * @param {Object} markerData
 * @param {{ zoom: number }} context
 * @returns {'price' | 'pin'}
 */
export function resolveMode(markerData, context) {
  const canUsePriceMode =
    context.zoom >= PRICE_PIN_ZOOM_LEVEL &&
    !!markerData.displayText;

  return canUsePriceMode ? 'price' : 'pin';
}

/**
 * Determines whether a basic marker should be visible given the current
 * zoom and default viewport visibility.
 *
 * The zoom threshold is only enforced when the location type is Market or City
 * AND there are more than PAID_MARKERS_THRESHOLD paid results. Otherwise,
 * basic markers are shown at any zoom level.
 *
 * @param {number} zoom - current map zoom level
 * @param {boolean} defaultVisibility - viewport-based default visibility
 * @param {{ isMarketOrCity?: boolean, paidCount?: number }} [context]
 * @returns {boolean}
 */
export function isBasicMarkerVisible(zoom, defaultVisibility, { isMarketOrCity = false, paidCount = 0 } = {}) {
  const applyThreshold = isMarketOrCity && paidCount > PAID_MARKERS_THRESHOLD;

  if (applyThreshold && zoom < BASIC_PIN_ZOOM_LEVEL) {
    return false;
  }

  return defaultVisibility;
}

/**
 * Determines whether a marker that belongs to a community with a site plan
 * should be visible at the current zoom level.
 *
 * Markers are hidden once the site plan overlay activates (zoom ≥ SITE_PLAN_RENDER_ZOOM_THRESHOLD)
 * so the site plan itself becomes the primary visual representation.
 * They reappear when zooming back out below the threshold.
 *
 * @param {Object} markerData
 * @param {number} zoom - current map zoom level
 * @returns {boolean}
 */
export function isSitePlanMarkerVisible(markerData, zoom) {
  const hasSitePlan = markerData.sitePlanType &&
    markerData.sitePlanType !== SitePlanType.None.toString();

  if (!hasSitePlan) {
    return true;
  }

  return zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD;
}

/**
 * Determines if the Community ISM indicator should be shown for a marker.
 *
 * @param {Object} markerData - Marker data object
 * @param {number} zoom - Current map zoom level
 * @returns {boolean}
 */
export function shouldShowCommunityISMIndicator(markerData, zoom) {
  return (
    markerData?.type === MarkerType.HousingDevelopment.toString() &&
    markerData.sitePlanType === SitePlanType.Interactive.toString() &&
    zoom >= INTERACTIVE_SITE_MAP_ZOOM
  );
}
